import logger from '@/utils/logger';
/**
 * EventBus - Système d'événements personnalisés pour la synchronisation des composants
 *
 * Permet aux composants de communiquer sans couplage direct via des événements personnalisés.
 * Utilisé pour synchroniser les assignations entre Bus, Chauffeurs et Circuits.
 */

class EventBus {
  constructor() {
    this.events = new Map();
    this.debugMode = import.meta.env.DEV || false;
  }

  /**
   * Émettre un événement personnalisé
   * @param {string} eventName - Nom de l'événement
   * @param {any} data - Données à transmettre avec l'événement
   */
  emit(eventName, data = null) {
    if (this.debugMode) {
      logger.debug(`🔔 [EventBus] Émission événement: ${eventName}`, data);
    }

    // Créer et dispatcher l'événement personnalisé
    const customEvent = new CustomEvent(eventName, {
      detail: {
        data,
        timestamp: new Date().toISOString(),
        eventName,
      },
    });

    window.dispatchEvent(customEvent);

    // Garder une trace des événements émis (pour debug)
    if (this.debugMode) {
      if (!this.events.has(eventName)) {
        this.events.set(eventName, []);
      }
      this.events.get(eventName).push({
        data,
        timestamp: new Date().toISOString(),
      });
    }
  }

  /**
   * Écouter un événement personnalisé
   * @param {string} eventName - Nom de l'événement à écouter
   * @param {Function} callback - Fonction à appeler quand l'événement est émis
   * @returns {Function} Fonction de nettoyage pour supprimer l'écouteur
   */
  on(eventName, callback) {
    if (this.debugMode) {
      logger.debug(`👂 [EventBus] Ajout écouteur pour: ${eventName}`);
    }

    const wrappedCallback = (event) => {
      if (this.debugMode) {
        logger.debug(`📨 [EventBus] Réception événement: ${eventName}`, event.detail);
      }
      callback(event.detail);
    };

    window.addEventListener(eventName, wrappedCallback);

    // Retourner une fonction de nettoyage
    return () => {
      if (this.debugMode) {
        logger.debug(`🧹 [EventBus] Suppression écouteur pour: ${eventName}`);
      }
      window.removeEventListener(eventName, wrappedCallback);
    };
  }

  /**
   * Supprimer un écouteur d'événement
   * @param {string} eventName - Nom de l'événement
   * @param {Function} callback - Fonction callback à supprimer
   */
  off(eventName, callback) {
    if (this.debugMode) {
      logger.debug(`❌ [EventBus] Suppression écouteur pour: ${eventName}`);
    }
    window.removeEventListener(eventName, callback);
  }

  /**
   * Supprimer tous les écouteurs d'un événement
   * @param {string} eventName - Nom de l'événement
   */
  removeAllListeners(eventName) {
    if (this.debugMode) {
      logger.debug(`🧹 [EventBus] Suppression de tous les écouteurs pour: ${eventName}`);
    }
    // Note: Il n'y a pas de moyen direct de supprimer tous les écouteurs d'un événement
    // Cette méthode est principalement pour la documentation
    logger.warn(
      `[EventBus] removeAllListeners(${eventName}) - Utilisez les fonctions de nettoyage retournées par on()`,
    );
  }

  /**
   * Obtenir l'historique des événements (mode debug uniquement)
   * @param {string} eventName - Nom de l'événement (optionnel)
   * @returns {Map|Array} Historique des événements
   */
  getEventHistory(eventName = null) {
    if (!this.debugMode) {
      logger.warn('[EventBus] getEventHistory() disponible uniquement en mode debug');
      return null;
    }

    if (eventName) {
      return this.events.get(eventName) || [];
    }
    return this.events;
  }

  /**
   * Vider l'historique des événements
   */
  clearHistory() {
    if (this.debugMode) {
      logger.debug('🧹 [EventBus] Nettoyage historique des événements');
      this.events.clear();
    }
  }

  /**
   * Activer/désactiver le mode debug
   * @param {boolean} enabled - Activer le mode debug
   */
  setDebugMode(enabled) {
    this.debugMode = enabled;
    logger.debug(`🐛 [EventBus] Mode debug: ${enabled ? 'ACTIVÉ' : 'DÉSACTIVÉ'}`);
  }
}

// Créer une instance singleton
const eventBus = new EventBus();

// Événements prédéfinis pour les assignations
export const ASSIGNMENT_EVENTS = {
  // Événements Bus
  BUS_ASSIGNED: 'bus:assigned',
  BUS_UNASSIGNED: 'bus:unassigned',
  BUS_UPDATED: 'bus:updated',
  BUS_CREATED: 'bus:created',
  BUS_DELETED: 'bus:deleted',

  // Événements Chauffeur
  DRIVER_ASSIGNED: 'driver:assigned',
  DRIVER_UNASSIGNED: 'driver:unassigned',
  DRIVER_UPDATED: 'driver:updated',
  DRIVER_CREATED: 'driver:created',
  DRIVER_DELETED: 'driver:deleted',

  // Événements Circuit/Route
  ROUTE_ASSIGNED: 'route:assigned',
  ROUTE_UNASSIGNED: 'route:unassigned',
  ROUTE_UPDATED: 'route:updated',
  ROUTE_CREATED: 'route:created',
  ROUTE_DELETED: 'route:deleted',

  // Événements d'assignation complexe
  ASSIGNMENT_CHANGED: 'assignment:changed',
  ASSIGNMENT_CONFLICT: 'assignment:conflict',
  ASSIGNMENT_RESOLVED: 'assignment:resolved',
};

// Fonctions utilitaires pour les assignations
export const AssignmentEventHelpers = {
  /**
   * Émettre un événement d'assignation de bus
   */
  emitBusAssignment(busId, driverId = null, routeId = null, action = 'assigned') {
    eventBus.emit(ASSIGNMENT_EVENTS.BUS_ASSIGNED, {
      busId,
      driverId,
      routeId,
      action,
      type: 'bus',
    });

    // Émettre aussi l'événement générique
    eventBus.emit(ASSIGNMENT_EVENTS.ASSIGNMENT_CHANGED, {
      type: 'bus',
      id: busId,
      assignedDriver: driverId,
      assignedRoute: routeId,
      action,
    });
  },

  /**
   * Émettre un événement d'assignation de chauffeur
   */
  emitDriverAssignment(driverId, busId = null, routeId = null, action = 'assigned') {
    eventBus.emit(ASSIGNMENT_EVENTS.DRIVER_ASSIGNED, {
      driverId,
      busId,
      routeId,
      action,
      type: 'driver',
    });

    eventBus.emit(ASSIGNMENT_EVENTS.ASSIGNMENT_CHANGED, {
      type: 'driver',
      id: driverId,
      assignedBus: busId,
      assignedRoute: routeId,
      action,
    });
  },

  /**
   * Émettre un événement d'assignation de circuit
   */
  emitRouteAssignment(routeId, busId = null, driverId = null, action = 'assigned') {
    eventBus.emit(ASSIGNMENT_EVENTS.ROUTE_ASSIGNED, {
      routeId,
      busId,
      driverId,
      action,
      type: 'route',
    });

    eventBus.emit(ASSIGNMENT_EVENTS.ASSIGNMENT_CHANGED, {
      type: 'route',
      id: routeId,
      assignedBus: busId,
      assignedDriver: driverId,
      action,
    });
  },
};

export default eventBus;
